import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, MapPin, ChevronDown, ShoppingCart, User,
  UtensilsCrossed, ArrowRight, Star, Clock,
  Home as HomeIcon, ListOrdered, X, ChevronRight,
  Leaf, Pizza, Coffee, Cookie, Sandwich, Soup,
  BadgePercent, Truck, Zap, Filter,
  Flame, Lock, Timer, Award, Bike,
  Tag, TrendingUp, Gift, ShoppingBasket,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

const SERVICES = [
  { label: "Food Delivery", Icon: UtensilsCrossed, route: "/restaurants", time: "30 min",  gradient: "linear-gradient(145deg,#b45309,#f97316)", glow: "rgba(249,115,22,0.35)" },
  { label: "Grocery",       Icon: ShoppingBasket,  route: "/grocery",     time: "10 min",  gradient: "linear-gradient(145deg,#166534,#22c55e)", glow: "rgba(34,197,94,0.3)"  },
];

const FOOD_CATS = [
  { label: "All",     Icon: UtensilsCrossed, color: "#f97316", q: ""        },
  { label: "Biryani", Icon: Soup,            color: "#d97706", q: "biryani" },
  { label: "Pizza",   Icon: Pizza,           color: "#ef4444", q: "pizza"   },
  { label: "Chicken", Icon: UtensilsCrossed, color: "#ea580c", q: "chicken" },
  { label: "Thali",   Icon: Soup,            color: "#7c3aed", q: "thali"   },
  { label: "Wraps",   Icon: Sandwich,        color: "#f97316", q: "wrap"    },
  { label: "Dessert", Icon: Cookie,          color: "#ec4899", q: "dessert" },
  { label: "Drinks",  Icon: Coffee,          color: "#0ea5e9", q: "drinks"  },
];

const OFFERS = [
  { id: 1, badge: "50% OFF",       title: "First Order Special", sub: "Use code FIRST50",     gradient: "linear-gradient(135deg,#7f1d1d,#c2410c,#f97316)", Icon: Gift    },
  { id: 2, badge: "FREE DELIVERY", title: "Orders Above ₹199",  sub: "No delivery charge",   gradient: "linear-gradient(135deg,#1e3a8a,#2563eb,#60a5fa)", Icon: Bike    },
  { id: 3, badge: "LUNCHTIME",     title: "Flat ₹40 OFF",       sub: "12 PM – 3 PM daily",   gradient: "linear-gradient(135deg,#14532d,#15803d,#4ade80)", Icon: Timer   },
];

const WHY = [
  { Icon: Timer,       color: "#f97316", title: "30 Min Delivery",   sub: "Lightning fast to your door" },
  { Icon: Award,       color: "#f59e0b", title: "Top Restaurants",   sub: "Best of Akola, curated"      },
  { Icon: BadgePercent,color: "#22c55e", title: "Best Prices",       sub: "No hidden charges ever"      },
  { Icon: Lock,        color: "#6366f1", title: "Safe Payments",     sub: "100% secure checkout"        },
];

const STATS = [
  { Icon: Bike,        color: "#f97316", val: "30 min", label: "Avg delivery" },
  { Icon: UtensilsCrossed, color: "#f97316", val: "20+",   label: "Restaurants"  },
  { Icon: Star,        color: "#f59e0b", val: "4.5",   label: "Avg rating"   },
  { Icon: Truck,       color: "#22c55e", val: "Free",   label: "Above ₹199"  },
];

const NAV = [
  { icon: HomeIcon,     label: "Home",    route: "/"            },
  { icon: Search,       label: "Explore", route: "/restaurants" },
  { icon: ShoppingCart, label: "Cart",    route: "/cart", cart: true },
  { icon: ListOrdered,  label: "Orders",  route: "/orders"      },
  { icon: User,         label: "Profile", route: "/profile"     },
];

function getNightMode() {
  const h = new Date().getHours();
  return h >= 20 || h < 6;
}

function Skel({ w, h, r = 8, style = {} }) {
  return <div className="skel" style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />;
}

function RestCardH({ r, dark, T, navigate, idx }) {
  return (
    <div
      className="tap nb"
      onClick={() => navigate(`/restaurant/${r._id}`)}
      style={{
        background: T.card, borderRadius: 20, display: "flex", gap: 12, padding: 12,
        border: `1px solid ${T.border}`, cursor: "pointer",
        animation: `fadeUp .4s ${idx * 0.07}s both`,
        boxShadow: dark ? "none" : "0 2px 14px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{
        width: 88, height: 88, borderRadius: 16, flexShrink: 0, overflow: "hidden",
        background: r.image ? `url(${r.image}) center/cover no-repeat` : "linear-gradient(135deg,#b45309,#f97316)",
        display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
      }}>
        {!r.image && <UtensilsCrossed size={28} color="rgba(255,255,255,0.15)" strokeWidth={1} />}
        {!r.isOpen && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: "0.52rem", fontWeight: 800, padding: "3px 8px", borderRadius: 100 }}>CLOSED</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div style={{ fontWeight: 800, fontSize: "0.92rem", color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
          {r.isVeg && (
            <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0, background: "#f0fdf4", border: "1.5px solid #16a34a", borderRadius: 5, padding: "1px 5px" }}>
              <Leaf size={8} color="#16a34a" /><span style={{ fontSize: "0.48rem", color: "#16a34a", fontWeight: 800 }}>VEG</span>
            </div>
          )}
        </div>
        <div style={{ fontSize: "0.72rem", color: T.sub, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {Array.isArray(r.cuisine) ? r.cuisine.join(" · ") : r.cuisine || r.category || "Multi-cuisine"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 3, background: "#fef9c3", border: "1px solid #fde047", borderRadius: 8, padding: "2px 8px", fontSize: "0.72rem", fontWeight: 800, color: "#854d0e" }}>
            <Star size={10} fill="#f59e0b" color="#f59e0b" /> {r.rating || "4.0"}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: "0.7rem", color: T.sub }}>
            <Clock size={10} color={T.muted} /> {r.deliveryTime || 30} min
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: "0.7rem", color: T.sub }}>
            <MapPin size={9} color={T.muted} /> {r.area || "Akola"}
          </span>
          {r.offer && (
            <span style={{ marginLeft: "auto", background: `${T.accent}15`, color: T.accent, fontSize: "0.62rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, border: `1px solid ${T.accent}30` }}>
              {r.offer}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { user, isLoggedIn } = useAuth();

  const [dark, setDark] = useState(getNightMode());
  const [restaurants, setRestaurants] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [loadingRest, setLoadingRest] = useState(true);
  const [banners, setBanners] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [offerIdx, setOfferIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [srchOpen, setSrchOpen] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [activecat, setActivecat] = useState("All");
  const srchRef = useRef(null);

  useEffect(() => {
    setDark(getNightMode());
    const iv = setInterval(() => setDark(getNightMode()), 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setBannerIdx(p => (p + 1) % Math.max(banners.length, 1)), 4500);
    return () => clearInterval(t);
  }, [banners.length]);

  useEffect(() => {
    const t = setInterval(() => setOfferIdx(p => (p + 1) % OFFERS.length), 3500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fn = () => setElevated(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/restaurants?limit=20&isActive=true`);
        const data = await res.json();
        if (data.success) {
          const list = data.data || data.restaurants || [];
          setRestaurants(list);
          setTopRated([...list].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 5));
        }
      } catch {}
      finally { setLoadingRest(false); }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/banners`);
        const data = await res.json();
        if (data.success && data.banners?.length > 0) setBanners(data.banners);
      } catch {}
    };
    load();
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/restaurants?q=${encodeURIComponent(search)}`); setSrchOpen(false); }
  };

  const greet = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 20) return "Good evening";
    return "Good night";
  };

  const T = dark ? {
    bg: "#080808", surface: "#101010", card: "#141414", card2: "#1a1a1a",
    border: "#1e1e1e", border2: "#282828", text: "#f8f8f8", sub: "#888",
    muted: "#444", accent: "#f97316", accentDim: "rgba(249,115,22,0.12)",
    glass: "rgba(10,10,10,0.96)", navBg: "rgba(8,8,8,0.97)",
    skelA: "#1c1c1c", skelB: "#232323",
  } : {
    bg: "#f7f7f7", surface: "#fff", card: "#fff", card2: "#fafafa",
    border: "#ebebeb", border2: "#e0e0e0", text: "#111", sub: "#666",
    muted: "#aaa", accent: "#f97316", accentDim: "rgba(249,115,22,0.09)",
    glass: "rgba(255,255,255,0.97)", navBg: "rgba(255,255,255,0.97)",
    skelA: "#efefef", skelB: "#e5e5e5",
  };

  const filteredRests = activecat === "All"
    ? restaurants
    : restaurants.filter(r => {
      const cuisine = (Array.isArray(r.cuisine) ? r.cuisine.join(" ") : r.cuisine || r.category || "").toLowerCase();
      return cuisine.includes(FOOD_CATS.find(c => c.label === activecat)?.q || "");
    });

  const bann = banners[bannerIdx];
  const offer = OFFERS[offerIdx];
  const OfferIcon = offer.Icon;

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif", background: T.bg, minHeight: "100vh", color: T.text, overflowX: "hidden", paddingBottom: 88, transition: "background .3s,color .3s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800;9..40,900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        html{scroll-behavior:smooth}
        button,input{font-family:inherit;outline:none}
        @keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes scalePop{from{transform:scale(.96);opacity:0}to{transform:scale(1);opacity:1}}
        @keyframes shimmer{0%{background-position:-700px 0}100%{background-position:700px 0}}
        .skel{background:linear-gradient(90deg,${T.skelA} 25%,${T.skelB} 50%,${T.skelA} 75%);background-size:700px 100%;animation:shimmer 1.5s infinite linear}
        .a1{animation:fadeUp .5s .00s both}
        .a2{animation:fadeUp .5s .07s both}
        .a3{animation:fadeUp .5s .14s both}
        .a4{animation:fadeUp .5s .21s both}
        .a5{animation:fadeUp .5s .28s both}
        .a6{animation:fadeUp .5s .35s both}
        .tap{transition:transform .15s,opacity .15s;cursor:pointer}.tap:active{transform:scale(.96);opacity:.82}
        .nb{-webkit-tap-highlight-color:transparent;cursor:pointer}.nb:active{opacity:.72}
      `}</style>

      {/* ── HEADER ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 500,
        background: elevated ? T.glass : "transparent",
        backdropFilter: elevated ? "blur(28px)" : "none",
        borderBottom: elevated ? `1px solid ${T.border}` : "none",
        transition: "all .25s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px" }}>
          <div style={{ fontWeight: 900, fontSize: "1.6rem", letterSpacing: "-1.5px", flexShrink: 0, color: T.text }}>
            Quick<span style={{ color: T.accent }}>ry</span>
          </div>
          <div className="nb tap" style={{ display: "flex", alignItems: "center", gap: 3, background: T.accentDim, border: `1px solid ${T.border2}`, borderRadius: 100, padding: "5px 10px", marginLeft: 2 }}>
            <MapPin size={10} color={T.accent} strokeWidth={2.5} />
            <span style={{ fontSize: "0.66rem", fontWeight: 700, color: T.text }}>Akola, MH</span>
            <ChevronDown size={9} color={T.muted} />
          </div>
          <div style={{ flex: 1 }} />
          <button className="nb tap" onClick={() => { setSrchOpen(true); setTimeout(() => srchRef.current?.focus(), 80); }}
            style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 13, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Search size={17} color={T.sub} />
          </button>
          <button className="nb tap" onClick={() => navigate("/cart")}
            style={{ position: "relative", background: T.accentDim, border: `1px solid ${T.accent}30`, borderRadius: 13, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShoppingCart size={17} color={T.accent} />
            {itemCount > 0 && (
              <div style={{ position: "absolute", top: -5, right: -5, background: T.accent, color: "#fff", minWidth: 18, height: 18, borderRadius: 100, fontSize: "0.54rem", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                {itemCount > 9 ? "9+" : itemCount}
              </div>
            )}
          </button>
          <button className="nb tap" onClick={() => navigate(isLoggedIn ? "/profile" : "/login")}
            style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 13, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <User size={17} color={T.sub} />
          </button>
        </div>
      </div>

      {/* ── SEARCH OVERLAY ── */}
      {srchOpen && (
        <div onClick={() => setSrchOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 600, backdropFilter: "blur(4px)", animation: "fadeIn .15s" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: T.glass, padding: "16px 16px 22px", boxShadow: "0 12px 50px rgba(0,0,0,0.3)", backdropFilter: "blur(30px)", animation: "fadeUp .2s" }}>
            <form onSubmit={doSearch}
              style={{ display: "flex", alignItems: "center", gap: 10, background: T.card, border: `2px solid ${T.accent}`, borderRadius: 16, padding: "12px 14px", marginBottom: 18, boxShadow: `0 0 0 4px ${T.accentDim}` }}>
              <Search size={17} color={T.accent} />
              <input ref={srchRef} value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search restaurants, cuisine..."
                style={{ flex: 1, border: "none", background: "transparent", fontSize: "0.92rem", color: T.text, fontWeight: 500 }} />
              {search
                ? <button type="button" onClick={() => setSearch("")} style={{ background: "none", border: "none" }}><X size={15} color={T.muted} /></button>
                : <button type="button" onClick={() => setSrchOpen(false)} style={{ background: "none", border: "none", color: T.accent, fontWeight: 800, fontSize: "0.8rem" }}>Cancel</button>
              }
            </form>
            <div style={{ fontSize: "0.6rem", fontWeight: 800, color: T.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: 6 }}>
              <TrendingUp size={10} color={T.muted} /> Trending searches
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["Biryani", "Pizza", "Chicken", "Thali", "Burger", "Noodles", "Paratha"].map(s => (
                <button key={s} className="nb tap" onClick={() => { navigate(`/restaurants?q=${s}`); setSrchOpen(false); }}
                  style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 100, padding: "7px 16px", fontSize: "0.78rem", fontWeight: 600, color: T.sub }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: "0 16px" }}>

        {/* ── GREETING ── */}
        <div className="a1" style={{ padding: "18px 0 16px" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: T.accent, letterSpacing: "0.5px", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 18, height: 2, background: T.accent, borderRadius: 2, display: "inline-block" }} />
            FOOD DELIVERY · AKOLA
          </div>
          <div style={{ fontWeight: 900, fontSize: "1.75rem", letterSpacing: "-0.5px", lineHeight: 1.18, color: T.text }}>
            {greet()}{isLoggedIn && user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
          </div>
          <div style={{ fontSize: "0.85rem", color: T.sub, marginTop: 6, lineHeight: 1.5 }}>
            Order from the best restaurants in Akola
          </div>
        </div>

        {/* ── SEARCH BAR ── */}
        <div className="a2" style={{ marginBottom: 20 }}>
          <div className="nb tap" onClick={() => { setSrchOpen(true); setTimeout(() => srchRef.current?.focus(), 80); }}
            style={{ display: "flex", alignItems: "center", gap: 12, background: T.card, border: `1.5px solid ${T.border2}`, borderRadius: 16, padding: "13px 16px", boxShadow: dark ? "none" : "0 2px 12px rgba(0,0,0,0.07)" }}>
            <Search size={17} color={T.muted} />
            <span style={{ fontSize: "0.88rem", color: T.muted, fontWeight: 500, flex: 1 }}>Search restaurants, cuisine...</span>
            <div style={{ background: T.accentDim, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4 }}>
              <Filter size={11} color={T.accent} />
              <span style={{ fontSize: "0.62rem", fontWeight: 700, color: T.accent }}>Filter</span>
            </div>
          </div>
        </div>


        {/* ── SERVICES GRID ── */}
        <div className="a2" style={{ marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {SERVICES.map((s) => (
              <div key={s.label} className="tap nb" onClick={() => navigate(s.route)}
                style={{ background: s.gradient, borderRadius: 20, padding: "18px 16px", cursor: "pointer", position: "relative", overflow: "hidden", boxShadow: `0 6px 24px ${s.glow}` }}>
                <div style={{ position: "absolute", width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.08)", right: -20, top: -20 }} />
                <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <s.Icon size={22} color="#fff" strokeWidth={1.7} />
                </div>
                <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "#fff", marginBottom: 3 }}>{s.label}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.65rem", color: "rgba(255,255,255,0.75)" }}>
                  <Timer size={10} color="rgba(255,255,255,0.75)" />
                  {s.time} delivery
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* ── BANNER ── */}
        <div className="a3" style={{ marginBottom: 20 }}>
          {bann ? (
            <div key={bann._id} className="tap nb" onClick={() => navigate(bann.route || "/restaurants")}
              style={{ background: bann.image ? `url(${bann.image}) center/cover no-repeat` : "linear-gradient(135deg,#7f1d1d,#c2410c,#f97316)", borderRadius: 24, padding: "24px 22px", minHeight: 170, position: "relative", overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.18)", animation: "scalePop .4s ease" }}>
              {bann.image && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,rgba(0,0,0,0.65),rgba(0,0,0,0.15))", borderRadius: 24 }} />}
              <div style={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: "rgba(255,255,255,0.06)", right: -80, top: -80 }} />
              {bann.badge && <div style={{ display: "inline-block", background: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)", color: "#fff", padding: "4px 12px", borderRadius: 100, fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.8px", marginBottom: 14, border: "1px solid rgba(255,255,255,0.2)", position: "relative" }}>{bann.badge}</div>}
              <div style={{ fontWeight: 900, fontSize: "2.4rem", color: "#fff", lineHeight: 1.05, letterSpacing: "-2px", marginBottom: 8, maxWidth: "65%", position: "relative" }}>{bann.title}</div>
              <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.82)", marginBottom: 18, maxWidth: "65%", lineHeight: 1.55, position: "relative" }}>{bann.subtitle}</div>
              <div className="tap" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fff", borderRadius: 100, padding: "9px 18px", position: "relative" }}>
                <span style={{ color: "#c2410c", fontWeight: 800, fontSize: "0.8rem" }}>Order now</span>
                <ArrowRight size={14} color="#c2410c" />
              </div>
            </div>
          ) : (
            <div key={offer.id} className="tap nb" onClick={() => navigate("/restaurants")}
              style={{ background: offer.gradient, borderRadius: 24, padding: "24px 22px", minHeight: 170, position: "relative", overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.18)", animation: "scalePop .35s ease" }}>
              <div style={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: "rgba(255,255,255,0.06)", right: -70, top: -80 }} />
              <div style={{ position: "absolute", right: 22, top: "50%", transform: "translateY(-50%)", width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <OfferIcon size={38} color="rgba(255,255,255,0.7)" strokeWidth={1.2} />
              </div>
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.18)", color: "#fff", padding: "3px 10px", borderRadius: 100, fontSize: "0.6rem", fontWeight: 800, marginBottom: 10, backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.18)", position: "relative" }}>{offer.badge}</div>
              <div style={{ fontWeight: 900, fontSize: "2rem", color: "#fff", lineHeight: 1.1, letterSpacing: "-1px", marginBottom: 6, position: "relative", maxWidth: "60%" }}>{offer.title}</div>
              <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.8)", marginBottom: 18, position: "relative" }}>{offer.sub}</div>
              <div className="tap" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.22)", backdropFilter: "blur(10px)", borderRadius: 100, padding: "9px 18px", border: "1px solid rgba(255,255,255,0.22)", position: "relative" }}>
                <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.8rem" }}>Order now</span>
                <ArrowRight size={14} color="#fff" />
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 10 }}>
            {(banners.length > 0 ? banners : OFFERS).map((_, i) => (
              <div key={i} onClick={() => banners.length > 0 ? setBannerIdx(i) : setOfferIdx(i)}
                style={{ width: i === (banners.length > 0 ? bannerIdx : offerIdx) ? 22 : 6, height: 6, borderRadius: 100, background: i === (banners.length > 0 ? bannerIdx : offerIdx) ? T.accent : T.border, cursor: "pointer", transition: "all .35s" }} />
            ))}
          </div>
        </div>

        {/* ── STATS STRIP ── */}
        <div className="a3" style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {STATS.map((s, i) => (
              <div key={i} style={{ flexShrink: 0, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "12px 16px", textAlign: "center", minWidth: 78, boxShadow: dark ? "none" : "0 1px 6px rgba(0,0,0,0.04)" }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 6px" }}>
                  <s.Icon size={16} color={s.color} strokeWidth={1.8} />
                </div>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: T.text }}>{s.val}</div>
                <div style={{ fontSize: "0.58rem", color: T.muted, marginTop: 2, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CATEGORY PILLS ── */}
        <div className="a3" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
            {FOOD_CATS.map(c => {
              const isActive = activecat === c.label;
              return (
                <div key={c.label} className="tap nb" onClick={() => setActivecat(c.label)}
                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, background: isActive ? T.accent : T.card, border: `1.5px solid ${isActive ? T.accent : T.border2}`, borderRadius: 100, padding: "8px 16px", boxShadow: isActive ? `0 4px 16px ${T.accent}40` : "none", transition: "all .2s" }}>
                  <div style={{ width: 22, height: 22, borderRadius: 7, background: isActive ? "rgba(255,255,255,0.2)" : `${c.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <c.Icon size={11} color={isActive ? "#fff" : c.color} strokeWidth={2} />
                  </div>
                  <span style={{ fontSize: "0.76rem", fontWeight: 700, color: isActive ? "#fff" : T.sub, whiteSpace: "nowrap" }}>{c.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── TRENDING NOW ── */}
        <div className="a4" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 9, background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Flame size={14} color={T.accent} />
                </div>
                <div style={{ fontWeight: 800, fontSize: "1.05rem", color: T.text }}>Trending Now</div>
              </div>
              <div style={{ fontSize: "0.68rem", color: T.sub, marginTop: 3, marginLeft: 36 }}>Top picks in Akola right now</div>
            </div>
            <button className="nb tap" onClick={() => navigate("/restaurants")}
              style={{ display: "flex", alignItems: "center", gap: 4, background: T.accentDim, border: `1px solid ${T.border2}`, borderRadius: 100, padding: "7px 14px", color: T.accent, fontWeight: 700, fontSize: "0.72rem" }}>
              See all <ChevronRight size={12} />
            </button>
          </div>

          <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
            {loadingRest ? (
              [0, 1, 2, 3].map(i => (
                <div key={i} style={{ flexShrink: 0, width: 190, background: T.card, borderRadius: 20, overflow: "hidden", border: `1px solid ${T.border}` }}>
                  <Skel w="100%" h={115} r={0} />
                  <div style={{ padding: 12 }}>
                    <Skel w="72%" h={13} style={{ marginBottom: 8 }} />
                    <Skel w="50%" h={10} style={{ marginBottom: 8 }} />
                    <div style={{ display: "flex", gap: 8 }}><Skel w={40} h={10} /><Skel w={40} h={10} /></div>
                  </div>
                </div>
              ))
            ) : restaurants.slice(0, 8).map((r, idx) => (
              <div key={r._id} className="tap nb" onClick={() => navigate(`/restaurant/${r._id}`)}
                style={{ flexShrink: 0, width: 190, background: T.card, borderRadius: 20, overflow: "hidden", border: `1px solid ${T.border}`, animation: `fadeUp .4s ${idx * .06}s both`, boxShadow: dark ? "none" : "0 2px 14px rgba(0,0,0,0.07)" }}>
                <div style={{ height: 115, background: r.image ? `url(${r.image}) center/cover no-repeat` : "linear-gradient(135deg,#b45309,#f97316)", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {!r.image && <UtensilsCrossed size={36} color="rgba(255,255,255,0.1)" strokeWidth={1} />}
                  {r.offer && (
                    <div style={{ position: "absolute", bottom: 8, left: 8, background: "linear-gradient(135deg,#f97316,#ef4444)", padding: "3px 10px", borderRadius: 100 }}>
                      <span style={{ fontSize: "0.56rem", fontWeight: 800, color: "#fff" }}>{r.offer}</span>
                    </div>
                  )}
                  {r.isVeg && (
                    <div style={{ position: "absolute", top: 8, right: 8, background: "#16a34a", display: "flex", alignItems: "center", gap: 2, padding: "2px 7px", borderRadius: 100 }}>
                      <Leaf size={8} color="#fff" /><span style={{ fontSize: "0.5rem", fontWeight: 800, color: "#fff" }}>VEG</span>
                    </div>
                  )}
                  {!r.isOpen && (
                    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.52)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: "0.62rem", fontWeight: 800, padding: "4px 10px", borderRadius: 100 }}>CLOSED</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "11px 12px 13px" }}>
                  <div style={{ fontWeight: 800, fontSize: "0.88rem", color: T.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                  <div style={{ fontSize: "0.7rem", color: T.sub, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {Array.isArray(r.cuisine) ? r.cuisine.join(" · ") : r.cuisine || "Multi-cuisine"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 3, background: "#fef9c3", borderRadius: 7, padding: "2px 7px", fontSize: "0.7rem", fontWeight: 800, color: "#854d0e" }}>
                      <Star size={9} fill="#f59e0b" color="#f59e0b" /> {r.rating || "4.0"}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: "0.68rem", color: T.muted }}>
                      <Clock size={9} /> {r.deliveryTime || 30}m
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── ALL RESTAURANTS ── */}
        <div className="a5" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 9, background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <UtensilsCrossed size={14} color={T.accent} />
                </div>
                <div style={{ fontWeight: 800, fontSize: "1.05rem", color: T.text }}>
                  {activecat === "All" ? "All Restaurants" : `${activecat} Places`}
                </div>
              </div>
              <div style={{ fontSize: "0.68rem", color: T.sub, marginTop: 3, marginLeft: 36 }}>
                {filteredRests.length} restaurants near you
              </div>
            </div>
          </div>

          {loadingRest ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[0, 1, 2, 3].map(i => (
                <div key={i} style={{ background: T.card, borderRadius: 20, display: "flex", gap: 12, padding: 12, border: `1px solid ${T.border}` }}>
                  <Skel w={88} h={88} r={16} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
                    <Skel w="60%" h={14} /><Skel w="42%" h={11} />
                    <div style={{ display: "flex", gap: 8 }}><Skel w={50} h={11} /><Skel w={50} h={11} /></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredRests.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", background: T.card, borderRadius: 20, border: `1px solid ${T.border}` }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: T.accentDim, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <UtensilsCrossed size={26} color={T.accent} strokeWidth={1.5} />
              </div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: T.text, marginBottom: 6 }}>No restaurants found</div>
              <div style={{ fontSize: "0.78rem", color: T.sub }}>Try a different category</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filteredRests.map((r, i) => (
                <RestCardH key={r._id} r={r} dark={dark} T={T} navigate={navigate} idx={i} />
              ))}
            </div>
          )}

          {filteredRests.length > 0 && (
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <button className="nb tap" onClick={() => navigate("/restaurants")}
                style={{ background: T.card, border: `1.5px solid ${T.border2}`, borderRadius: 100, padding: "12px 28px", color: T.text, fontWeight: 700, fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: 7 }}>
                View all restaurants <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* ── TOP RATED ── */}
        {topRated.length > 0 && (
          <div className="a5" style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: 9, background: "#fef9c320", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Award size={14} color="#f59e0b" />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: "1.05rem", color: T.text }}>Top Rated</div>
                <div style={{ fontSize: "0.68rem", color: T.sub, marginTop: 2 }}>Highest rated in Akola</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {topRated.map((r, i) => (
                <RestCardH key={r._id} r={r} dark={dark} T={T} navigate={navigate} idx={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── WHY QUICKRY ── */}
        <div className="a6" style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 800, fontSize: "1.05rem", color: T.text, marginBottom: 14 }}>Why choose Quickry?</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {WHY.map((w, i) => (
              <div key={i} style={{ background: T.card, borderRadius: 20, padding: "18px 14px", border: `1px solid ${T.border}`, boxShadow: dark ? "none" : "0 2px 10px rgba(0,0,0,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 14, background: `${w.color}15`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <w.Icon size={20} color={w.color} strokeWidth={1.8} />
                </div>
                <div style={{ fontWeight: 800, fontSize: "0.88rem", color: T.text, marginBottom: 4 }}>{w.title}</div>
                <div style={{ fontSize: "0.72rem", color: T.sub, lineHeight: 1.5 }}>{w.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── PROMO BANNER ── */}
        <div className="a6" style={{ marginBottom: 24 }}>
          <div className="tap nb" onClick={() => navigate("/restaurants")}
            style={{ background: dark ? "linear-gradient(135deg,#1c0a00,#431407,#7c2d12)" : "linear-gradient(135deg,#fff7ed,#ffedd5,#fed7aa)", borderRadius: 24, padding: "20px 20px", border: `1px solid ${dark ? "#431407" : "#fdba74"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: "0.62rem", fontWeight: 800, color: T.accent, letterSpacing: "1px", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <Gift size={11} color={T.accent} /> SPECIAL OFFER
              </div>
              <div style={{ fontWeight: 900, fontSize: "1.4rem", color: T.text, lineHeight: 1.2, marginBottom: 4 }}>First order<br />50% OFF</div>
              <div style={{ fontSize: "0.74rem", color: T.sub, marginBottom: 16 }}>Use code <span style={{ fontWeight: 800, color: T.accent }}>FIRST50</span></div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.accent, borderRadius: 100, padding: "9px 18px", boxShadow: `0 6px 20px ${T.accent}40` }}>
                <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.8rem" }}>Order now</span>
                <ArrowRight size={14} color="#fff" />
              </div>
            </div>
            <div style={{ width: 90, height: 90, borderRadius: "50%", background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UtensilsCrossed size={42} color={T.accent} strokeWidth={1.2} />
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{ textAlign: "center", padding: "24px 0 12px", borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontWeight: 900, fontSize: "1.6rem", letterSpacing: "-1.5px", marginBottom: 4, color: T.text }}>
            Quick<span style={{ color: T.accent }}>ry</span>
          </div>
          <div style={{ fontSize: "0.7rem", color: T.muted, lineHeight: 1.8, marginBottom: 16 }}>
            Akola ka apna food delivery app<br />Fast · Fresh · Affordable
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            {[
              { label: "About",   route: "/about"          },
              { label: "Contact", route: "/contact"        },
              { label: "Privacy", route: "/privacy-policy" },
              { label: "Terms",   route: "/terms"          },
              { label: "Refund",  route: "/refund-policy"  },
            ].map(l => (
              <span key={l.label} className="nb" onClick={() => navigate(l.route)}
                style={{ fontSize: "0.7rem", color: T.muted, cursor: "pointer", fontWeight: 500 }}>{l.label}</span>
            ))}
          </div>
          <div style={{ marginTop: 12, fontSize: "0.62rem", color: T.muted }}>
            © 2024 Quickry, Akola, Maharashtra
          </div>
        </div>

      </div>

      {/* ── BOTTOM NAV ── */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: T.navBg, backdropFilter: "blur(28px)", borderTop: `1px solid ${T.border}`, display: "flex", zIndex: 500, boxShadow: `0 -4px 24px rgba(0,0,0,${dark ? 0.5 : 0.07})`, paddingBottom: "env(safe-area-inset-bottom,0px)", transition: "background .3s" }}>
        {NAV.map(({ icon: Icon, label, route, cart: isCart }) => {
          const active = window.location.pathname === route;
          return (
            <button key={route} className="nb" onClick={() => navigate(route)}
              style={{ flex: 1, background: "none", border: "none", padding: "10px 0 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Icon size={22} color={active ? T.accent : T.muted} strokeWidth={active ? 2.2 : 1.7} />
                {isCart && itemCount > 0 && (
                  <div style={{ position: "absolute", top: -5, right: -7, background: T.accent, color: "#fff", minWidth: 17, height: 17, borderRadius: 100, fontSize: "0.52rem", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                    {itemCount > 9 ? "9+" : itemCount}
                  </div>
                )}
              </div>
              <span style={{ fontSize: "0.58rem", fontWeight: active ? 800 : 500, color: active ? T.accent : T.muted }}>{label}</span>
              {active && <div style={{ position: "absolute", bottom: 0, width: 20, height: 3, background: T.accent, borderRadius: 100 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}